/**
 * Inactiviteitstimer voor chatters (03-10-2026, Jordy).
 *
 * Na 1 uur zonder echte activiteit wordt een chatter uitgelogd. Echte
 * activiteit = klikken (of tikken) en typen; verzenden gaat altijd via een klik
 * of Enter. Muisbewegen en scrollen tellen bewust NIET (Jordy: "loutere
 * muisbeweging telt niet"). Een open tabblad en het automatisch verversen van
 * het dashboard tellen ook niet.
 *
 * De laatste activiteit staat in localStorage, zodat alle tabbladen dezelfde
 * klok hebben: wie in tabblad A typt, wordt in tabblad B niet uitgelogd. Hooguit
 * eens per minuut meldt een tabblad die tijd aan de server (relatief, in
 * seconden geleden); de server weigert een chatter die langer dan een uur stil
 * is, ook als dit tabblad dicht was of de laptop sliep.
 */
import { useEffect, useState } from 'react'
import { api, uitloggen } from './api'
import { meldOffline } from './aanwezigheid'

export const INACTIEF_MS = 60 * 60_000
export const WAARSCHUWING_MS = 55 * 60_000
const MELD_MS = 60_000

const ACTIVITEIT = 'novahub_last_activity'
const GEMELD_ACT = 'novahub_activity_reported'
const GEMELD_OP = 'novahub_activity_reported_at'
export const UITLOG_REDEN = 'novahub_uitlog_reden'

function lees(sleutel: string): number {
  try { return Number(localStorage.getItem(sleutel) || 0) } catch { return 0 }
}

function schrijf(sleutel: string, waarde: number): void {
  try { localStorage.setItem(sleutel, String(waarde)) } catch { /* private mode: alleen dit tabblad */ }
}

/** Nu als laatste activiteit zetten (bij elke klik of toets, en bij inloggen). */
export function markeerActiviteit(nu: number = Date.now()): void {
  // Niet bij elke toetsaanslag schrijven; op vijf seconden nauwkeurig is genoeg.
  if (nu - lees(ACTIVITEIT) > 5_000) schrijf(ACTIVITEIT, nu)
}

/** Meld de laatste activiteit aan de server als er iets nieuws is en de vorige melding > 1 min oud. */
async function meldAlsNodig(nu: number = Date.now()): Promise<void> {
  const laatste = lees(ACTIVITEIT)
  if (!laatste || laatste <= lees(GEMELD_ACT) || nu - lees(GEMELD_OP) < MELD_MS) return
  schrijf(GEMELD_ACT, laatste)
  schrijf(GEMELD_OP, nu)
  try {
    await api.post('/api/admin/auth/activity', { inactief_sec: Math.max(0, Math.round((nu - laatste) / 1000)) })
  } catch { /* een gemiste melding: de volgende minuut opnieuw */ }
}

let bezigMetUitloggen = false

async function uitloggenWegensStilte(): Promise<void> {
  if (bezigMetUitloggen) return
  bezigMetUitloggen = true
  try { sessionStorage.setItem(UITLOG_REDEN, 'inactief') } catch { /* private mode */ }
  await meldOffline()
  await uitloggen()
  window.location.reload()
}

/**
 * Zet de timer aan voor een chatter. Geeft de resterende tijd (ms) terug zodra
 * de waarschuwing moet staan (laatste 5 minuten), anders null.
 */
export function useInactiviteit(aan: boolean): number | null {
  const [resterend, setResterend] = useState<number | null>(null)
  useEffect(() => {
    if (!aan) { setResterend(null); return }
    // Eerste keer in deze browser: de pagina openen na het inloggen telt.
    if (!lees(ACTIVITEIT)) markeerActiviteit()
    const bijActie = () => { markeerActiviteit(); void meldAlsNodig() }
    window.addEventListener('pointerdown', bijActie, true)
    window.addEventListener('keydown', bijActie, true)
    const controle = () => {
      const stil = Date.now() - lees(ACTIVITEIT)
      if (stil >= INACTIEF_MS) { void uitloggenWegensStilte(); return }
      setResterend(stil >= WAARSCHUWING_MS ? INACTIEF_MS - stil : null)
      void meldAlsNodig()
    }
    controle()
    const iv = window.setInterval(controle, 1_000)
    return () => {
      window.removeEventListener('pointerdown', bijActie, true)
      window.removeEventListener('keydown', bijActie, true)
      window.clearInterval(iv)
    }
  }, [aan])
  return resterend
}
